import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import Select, { SingleValue } from "react-select";
import { Row, Col, Button, Spinner, FormGroup, Label, Input } from "reactstrap";
import { Drawer } from "antd";
import rolesApi from "../../api/master/roles";
import ApprovalHeirarchyApis from "../../api/master/approvalHeirarchy";

interface Employee {
  name: string;
  id: string;
}

interface Role {
  roleName: string;
  employees: Employee[];
  roleID: any;
  level: string;
}

interface CustomDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ApprovalData[], isStrictApproval: boolean) => void;
  setApprovalSubmissionStatus: (status: boolean) => void;
  approvalData: ApprovalData[];
  siteId: number;
  initialStrictApproval?: boolean;
}

interface FormValues {
  [key: string]: string;
}

interface ApprovalData {
  roleId: any;
  level: string;
  userId: any;
}

const PurchaseOrderApprovalDrawer: React.FC<CustomDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  setApprovalSubmissionStatus,
  approvalData,
  siteId,
  initialStrictApproval = false,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [alertInfo, setAlertInfo] = useState<any>(null);

  const [isStrictApproval, setIsStrictApproval] = useState<boolean>(initialStrictApproval);
  const [apiStrictApproval, setApiStrictApproval] = useState<boolean | null>(null);
  const [hasFetchedApi, setHasFetchedApi] = useState<boolean>(false);

  const fetchApprovalHierarchyByModuleId = async (moduleId: number) => {
    try {
      const res = await ApprovalHeirarchyApis.fetchApprovalHierarchyByModuleId(
        moduleId,
        { params: { header: 1 } }
      );

      if (res?.status && res?.data) {
        const strictValue = res.data.isStrictApproval ?? res.data.isStrict ?? false;
        setApiStrictApproval(strictValue);

        if (!hasFetchedApi) {
          setIsStrictApproval(strictValue);
          setHasFetchedApi(true);
        }
      }
    } catch (err) {
      console.error("Failed to fetch approval hierarchy config:", err);
      setHasFetchedApi(true);
    }
  };

  const fetchApprovalHierarchyUsersByModuleId = async (
    moduleID: number,
    siteId: number
  ) => {
    try {
      const res = await rolesApi.fetchHierarchyUsersByModuleId(
        moduleID,
        siteId
      );
      if (!res) return;
      const { data, status, message } = res;
      if (status) {
        setRoles(data);
        setLoading(false);
      } else {
        setAlertInfo(message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const fetchModuleConfig = async () => {
    try {
      const res = await rolesApi.findAllModuleConfig();
      const moduleID = res?.data?.find(
        (x: any) => x.moduleName == "Purchase Order"
      )?.moduleID;
      if (moduleID) {
        await Promise.all([
          fetchApprovalHierarchyByModuleId(moduleID),
          fetchApprovalHierarchyUsersByModuleId(moduleID, siteId)
        ]);
      }
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchModuleConfig();
  }, []);

  useEffect(() => {
    if (open) {
      setIsStrictApproval(initialStrictApproval);
    }
  }, [open, initialStrictApproval]);

  useEffect(() => {
    roles.forEach((role, index) => {
      register(role.level, { required: "This field is required" }); // uncomment this line if you want to show the required field error
    });
  }, [roles, register]);

  useEffect(() => {
    if (approvalData && approvalData.length > 0) {
      approvalData.forEach((data, index) => {
        setValue(data.level, data.userId);
      });
      setLoading(false);
    }
  }, [approvalData, setValue]);

  const handleStrictApprovalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsStrictApproval(e.target.checked);
  };

  const submitApprovals: SubmitHandler<FormValues> = (data) => {
    const approvalData: ApprovalData[] = roles.map((role, index) => ({
      roleId: Number(role.roleID),
      level: role.level,
      userId: data[`${role.level}`],
    }));

    onSubmit(approvalData, isStrictApproval);
    setApprovalSubmissionStatus(true);
  };

  const handleSelectChange = (
    name: string,
    value: SingleValue<{ value: string; label: string }>
  ) => {
    if (value && value.value) {
      setValue(name, value.value, { shouldValidate: true });
    }
  };

  const getDefaultValue = (role: Role) => {
    const approval = approvalData?.find((data) => data.level === role.level);
    if (approval) {
      const employee = role.employees?.find(
        (emp) => emp.id === approval.userId
      );
      if (employee) {
        return { value: employee.id, label: employee.name };
      }
    }
    return null;
  };

  return (
    <Drawer
      title="Set Approvals"
      placement="right"
      onClose={onClose}
      open={open}
    >
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Spinner color="primary" />
        </div>
      ) : (
        <div>
          {alertInfo ? (
            <p className="text-center">{alertInfo}</p>
          ) : (
            <>
              <p>
                There are {roles.length} levels of approvals set for this
                process
              </p>

              {/* Strict Approval Checkbox */}
              <FormGroup check className="mb-3">
                <Label check>
                  <Input
                    type="checkbox"
                    checked={isStrictApproval}
                    onChange={handleStrictApprovalChange}
                  />{' '}
                  Strict Approvals
                </Label>
              </FormGroup>

              <form onSubmit={handleSubmit(submitApprovals)}>
                {roles.map((role, index) => (
                  <Row className="mt-2" key={index}>
                    <Col lg="12">
                      <label>{`${role.roleName}`}</label>
                      <Select
                        classNamePrefix="react-select"
                        options={role.employees?.map((employee) => ({
                          value: employee.id,
                          label: employee.name,
                        }))}
                        onChange={(selectedOption) =>
                          handleSelectChange(`${role.level}`, selectedOption)
                        }
                        placeholder={`Select ${role.roleName}`}
                        defaultValue={getDefaultValue(role)}
                      />
                      {errors[`${role.level}`] && (
                        <p className="mandatory">
                          {errors[`${role.level}`]?.message}
                        </p>
                      )}
                    </Col>
                  </Row>
                ))}
                <Button type="submit" color="primary" className="mt-3">
                  Save
                </Button>
              </form>
            </>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default React.memo(PurchaseOrderApprovalDrawer);
